import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../../../../config/firebase";

// ─── Constants ────────────────────────────────────────────────────────────────

// Abbreviated step titles for the 24-cell heatmap (matches stepConfig.ts order)
const STEP_SHORT_TITLES = [
  "Mkt Segmentation",   // 1
  "Beachhead Market",   // 2
  "End User Profile",   // 3
  "TAM (Beachhead)",    // 4
  "Profile Persona",    // 5
  "Lifecycle Use Case", // 6
  "Product Spec",       // 7
  "Value Prop",         // 8
  "Next 10 Customers",  // 9
  "Define Core",        // 10
  "Competitive Pos.",   // 11
  "Customer DMU",       // 12
  "Acquire Customer",   // 13
  "TAM (Follow-on)",    // 14
  "Business Model",     // 15
  "Pricing Framework",  // 16
  "Calculate LTV",      // 17
  "Sales Process",      // 18
  "Calculate COCA",     // 19
  "Key Assumptions",    // 20
  "Test Assumptions",   // 21
  "Define MVP",         // 22
  "Validation Test",    // 23
  "Product Plan",       // 24
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeedbackEntry {
  uid: string;
  projectID: string;
  projectTitle: string;
  whatWorking: string;
  whatToImprove: string;
  keyMetric: string;
  submittedAt?: string;
}

interface UserRow {
  uid: string;
  displayName?: string;
  email?: string;
  projectCount: number;
  hasScore: boolean;
  adminNote?: string;
}

interface ProjectRow {
  userUID: string;
  displayName: string;
  projectTitle: string;
  stepsCompleted: number;
  overallScore: number | null;
  createdAt: string;
  hasNotes: boolean;
}

interface AdminStats {
  totalUsers: number;
  totalProjects: number;
  totalWithScores: number;
  recentFeedback: FeedbackEntry[];
  /** 24-length array: stepCounts[i] = number of projects that have started step (i+1) */
  stepCounts: number[];
  userRows: UserRow[];
  projectRows: ProjectRow[];
}

interface EmailQueueItem {
  id: string;
  to: string;
  type: string;
  status: string;
  scheduledFor: string;
  processedAt?: string;
  firstName?: string;
  subject?: string;
}

// ─── Data loading ─────────────────────────────────────────────────────────────

async function loadAdminStats(): Promise<AdminStats> {
  // Load all users (capped at 100 for performance at this scale)
  const usersSnap = await getDocs(query(collection(db, "users"), limit(100)));

  let totalProjects = 0;
  let totalWithScores = 0;
  const recentFeedback: FeedbackEntry[] = [];
  const projectRows: ProjectRow[] = [];
  // 24-element array counting how many projects have each step started
  const stepCounts = new Array<number>(24).fill(0);

  const userStatPromises = usersSnap.docs.map(async (userDoc) => {
    const uid = userDoc.id;
    const uData = userDoc.data();
    const userRef = doc(db, "users", uid);

    // Get projects for this user
    let projectCount = 0;
    let hasScore = false;
    try {
      const projectsSnap = await getDocs(collection(userRef, "projects"));
      projectCount = projectsSnap.size;

      for (const projectDoc of projectsSnap.docs) {
        const pData = projectDoc.data();
        if (pData.businessScore) hasScore = true;

        // ── Step completion heatmap data + notes detection ────────────────
        let projectHasNotes = false;
        try {
          const progressRef = collection(
            db,
            "users",
            uid,
            "projects",
            projectDoc.id,
            "progress"
          );
          const progressSnap = await getDocs(progressRef);
          for (const stepDoc of progressSnap.docs) {
            const stepData = stepDoc.data();
            if ((stepData.stepProgress ?? 0) > 0) {
              // stepDoc.id is "step1".."step24"
              const stepNum = parseInt(stepDoc.id.replace("step", ""), 10);
              if (!isNaN(stepNum) && stepNum >= 1 && stepNum <= 24) {
                const idx = stepNum - 1;
                stepCounts[idx] = (stepCounts[idx] ?? 0) + 1;
              }
            }
            if (typeof stepData.notes === "string" && stepData.notes.trim()) {
              projectHasNotes = true;
            }
          }
        } catch {
          // Progress data unavailable for this project — skip silently
        }

        // ── CSV export row ────────────────────────────────────────────────
        projectRows.push({
          userUID: uid,
          displayName: (uData.displayName as string | undefined) ?? uid.slice(0, 8),
          projectTitle: (pData.projectTitle as string | undefined) ?? "Untitled",
          stepsCompleted: typeof pData.projectProgress === "number" ? pData.projectProgress : 0,
          overallScore: typeof pData.businessScore === "number" ? pData.businessScore : null,
          createdAt: typeof pData.createdAt === "string" ? pData.createdAt : "",
          hasNotes: projectHasNotes,
        });

        // ── Feedback collection ───────────────────────────────────────────
        try {
          const feedbackRef = doc(
            db,
            "users",
            uid,
            "projects",
            projectDoc.id,
            "feedback",
            "summary"
          );
          const feedbackSnap = await getDoc(feedbackRef);
          if (feedbackSnap.exists()) {
            const fb = feedbackSnap.data();
            recentFeedback.push({
              uid,
              projectID: projectDoc.id,
              projectTitle: pData.projectTitle ?? "Untitled",
              whatWorking: fb.whatWorking ?? "",
              whatToImprove: fb.whatToImprove ?? "",
              keyMetric: fb.keyMetric ?? "",
              submittedAt: fb.submittedAt,
            });
          }
        } catch {
          // Feedback may not exist — skip silently
        }
      }
    } catch {
      // No projects or permission issue — skip
    }

    return {
      uid,
      displayName: uData.displayName as string | undefined,
      email: uData.email as string | undefined,
      projectCount,
      hasScore,
      adminNote: uData.adminNote as string | undefined,
    };
  });

  const userStats = await Promise.all(userStatPromises);

  for (const u of userStats) {
    totalProjects += u.projectCount;
    if (u.hasScore) totalWithScores++;
  }

  // Sort feedback by submittedAt descending, take 10
  recentFeedback.sort((a, b) => {
    if (!a.submittedAt) return 1;
    if (!b.submittedAt) return -1;
    return b.submittedAt.localeCompare(a.submittedAt);
  });

  // Sort users by project count descending
  const userRows: UserRow[] = userStats.sort(
    (a, b) => b.projectCount - a.projectCount
  );

  return {
    totalUsers: usersSnap.size,
    totalProjects,
    totalWithScores,
    recentFeedback: recentFeedback.slice(0, 10),
    stepCounts,
    userRows,
    projectRows,
  };
}

// ─── Relative timestamp helper ────────────────────────────────────────────────

function timeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return "—";
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

// ─── Email Queue Loader ────────────────────────────────────────────────────────

async function loadEmailQueue(): Promise<EmailQueueItem[]> {
  try {
    const snap = await getDocs(
      query(collection(db, "emailQueue"), orderBy("scheduledFor", "desc"), limit(50))
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        to: (data.to as string | undefined) ?? "—",
        type: (data.type as string | undefined) ?? "—",
        status: (data.status as string | undefined) ?? "—",
        scheduledFor: (data.scheduledFor as string | undefined) ?? "",
        processedAt: (data.processedAt as string | undefined),
      };
    });
  } catch {
    return [];
  }
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

function downloadProjectsCSV(rows: ProjectRow[]): void {
  const header = ["userUID", "displayName", "projectTitle", "stepsCompleted", "overallScore", "createdAt", "hasNotes"];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [
    header.join(","),
    ...rows.map((r) =>
      [
        escape(r.userUID),
        escape(r.displayName),
        escape(r.projectTitle),
        String(r.stepsCompleted),
        r.overallScore !== null ? String(r.overallScore) : "",
        escape(r.createdAt),
        r.hasNotes ? "Yes" : "No",
      ].join(",")
    ),
  ];
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const a = document.createElement("a");
  a.href = url;
  a.download = `business-foundry-projects-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  userID: string;
}

export const AdminDashboardPage: React.FC<Props> = ({ userID }) => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [emailQueue, setEmailQueue] = useState<EmailQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userID) return;
    const check = async () => {
      try {
        const userRef = doc(db, "users", userID);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists() && userSnap.data().isAdmin === true) {
          setIsAdmin(true);
          const [s, eq] = await Promise.all([loadAdminStats(), loadEmailQueue()]);
          setStats(s);
          setEmailQueue(eq);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("Admin load error:", err);
        setError("Failed to load admin data.");
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [userID]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-primary" style={{ width: 32, height: 32 }}></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: 400, padding: 32 }}
      >
        <i className="bi bi-shield-lock" style={{ fontSize: 48, color: "#94A3B8", marginBottom: 16 }}></i>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>
          Access Denied
        </h3>
        <p style={{ fontSize: 15, color: "#475569", maxWidth: 360 }}>
          This page is restricted to admin users. Contact Enda if you need access.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="alert d-flex align-items-center"
        style={{ borderRadius: 12, background: "#FEE2E2", border: "1px solid #EF4444", padding: "16px 24px" }}
      >
        <i className="bi bi-exclamation-triangle me-3" style={{ color: "#EF4444", fontSize: 20 }}></i>
        <span style={{ color: "#0F172A", fontSize: 15 }}>{error}</span>
      </div>
    );
  }

  const s = stats!;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-8">
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em", marginBottom: 4 }}>
            Admin Dashboard
          </h1>
          <p style={{ fontSize: 15, color: "#475569", marginBottom: 0 }}>
            Platform usage overview — Business Foundry
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadProjectsCSV(s.projectRows)}
          disabled={s.projectRows.length === 0}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            background: s.projectRows.length === 0 ? "#F1F5F9" : "#3B82F6",
            color: s.projectRows.length === 0 ? "#94A3B8" : "#FFFFFF",
            border: "none",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "13px",
            cursor: s.projectRows.length === 0 ? "not-allowed" : "pointer",
            transition: "all 150ms ease-out",
            flexShrink: 0,
          }}
        >
          <i className="bi bi-download" style={{ fontSize: "14px" }} />
          Download CSV
        </button>
      </div>

      {/* Stats row */}
      <div className="row g-4 mb-8">
        <div className="col-sm-6 col-xl-3">
          <StatCard
            icon="bi-people"
            iconColor="#475569"
            iconBg="#F1F5F9"
            label="Registered Users"
            value={String(s.totalUsers)}
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard
            icon="bi-folder2"
            iconColor="#475569"
            iconBg="#F1F5F9"
            label="Total Projects"
            value={String(s.totalProjects)}
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard
            icon="bi-graph-up"
            iconColor="#475569"
            iconBg="#F1F5F9"
            label="Projects with AI Score"
            value={String(s.totalWithScores)}
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard
            icon="bi-chat-square-text"
            iconColor="#475569"
            iconBg="#F1F5F9"
            label="Feedback Submissions"
            value={String(s.recentFeedback.length)}
            note="shown (up to 10)"
          />
        </div>
      </div>

      {/* Step Completion Heatmap */}
      <StepHeatmap stepCounts={s.stepCounts} totalProjects={s.totalProjects} />

      {/* User List */}
      <UserList users={s.userRows} />

      {/* Recent feedback */}
      <div
        className="card"
        style={{
          border: "1px solid #E2E8F0",
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          marginBottom: 32,
        }}
      >
        <div
          className="card-header border-0 d-flex align-items-center justify-content-between"
          style={{ padding: "20px 24px 0" }}
        >
          <span style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>
            Recent Feedback
          </span>
          <a
            href="https://console.firebase.google.com/"
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm"
            style={{
              background: "transparent",
              color: "#3B82F6",
              border: "1px solid #3B82F6",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            Firebase Console
          </a>
        </div>

        <div className="card-body" style={{ padding: "16px 24px 24px" }}>
          {s.recentFeedback.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center text-center"
              style={{ padding: "32px 16px" }}
            >
              <i className="bi bi-inbox" style={{ fontSize: 48, color: "#94A3B8", marginBottom: 12 }}></i>
              <p style={{ fontSize: 15, color: "#475569" }}>
                No feedback submissions yet. Feedback appears here once users complete the Build phase.
              </p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {s.recentFeedback.map((fb, i) => (
                <FeedbackRow key={`${fb.uid}-${fb.projectID}-${i}`} feedback={fb} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Email Queue */}
      <EmailQueueCard items={emailQueue} />

      {/* Footer note */}
      <p style={{ fontSize: 13, color: "#94A3B8", textAlign: "center" }}>
        Data queried live from Firestore (capped at 100 users). For deeper analytics, use Firebase Console.
      </p>
    </div>
  );
};

// ─── StepHeatmap ─────────────────────────────────────────────────────────────

interface StepHeatmapProps {
  stepCounts: number[];
  totalProjects: number;
}

// Blue-intensity colour for heatmap cells (replaces traffic-light approach)
function heatmapCellColours(pct: number, hasData: boolean): { scoreColour: string; scoreBg: string } {
  if (!hasData) return { scoreColour: "#94A3B8", scoreBg: "#F8FAFC" };
  if (pct >= 75) return { scoreColour: "#1D4ED8", scoreBg: "#BFDBFE" };
  if (pct >= 50) return { scoreColour: "#3B82F6", scoreBg: "#DBEAFE" };
  if (pct >= 25) return { scoreColour: "#60A5FA", scoreBg: "#EFF6FF" };
  return { scoreColour: "#94A3B8", scoreBg: "#F8FAFC" };
}

const StepHeatmap: React.FC<StepHeatmapProps> = ({ stepCounts, totalProjects }) => {
  return (
    <div
      className="card mb-8"
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Card header */}
      <div
        className="card-header border-0 d-flex flex-wrap align-items-center justify-content-between gap-2"
        style={{ padding: "20px 24px 12px" }}
      >
        <div>
          <span style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>
            Step Completion Heatmap
          </span>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 0, marginTop: 2 }}>
            Which of the 24 steps users start — darker blue = higher adoption.
            {totalProjects > 0 && (
              <span style={{ color: "#94A3B8", marginLeft: 6 }}>
                ({totalProjects} project{totalProjects !== 1 ? "s" : ""} total)
              </span>
            )}
          </p>
        </div>

        {/* Legend — gradient strip */}
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <span style={{ fontSize: 11, color: "#94A3B8" }}>0%</span>
          <div
            style={{
              width: 80,
              height: 10,
              borderRadius: 5,
              background: "linear-gradient(to right, #F8FAFC, #EFF6FF, #DBEAFE, #3B82F6, #1D4ED8)",
              border: "1px solid #E2E8F0",
            }}
          />
          <span style={{ fontSize: 11, color: "#94A3B8" }}>100%</span>
        </div>
      </div>

      {/* Heatmap grid */}
      <div className="card-body" style={{ padding: "8px 20px 24px" }}>
        {totalProjects === 0 ? (
          <div
            className="d-flex flex-column align-items-center text-center"
            style={{ padding: "32px 16px" }}
          >
            <i className="bi bi-grid-3x3" style={{ fontSize: 48, color: "#94A3B8", marginBottom: 12 }}></i>
            <p style={{ fontSize: 15, color: "#475569" }}>
              No projects yet. The heatmap fills in as users complete validation steps.
            </p>
          </div>
        ) : (
          <div className="row g-2">
            {STEP_SHORT_TITLES.map((title, idx) => {
              const stepNum = idx + 1;
              const count = stepCounts[idx] ?? 0;
              const pct = totalProjects > 0 ? Math.round((count / totalProjects) * 100) : 0;
              const { scoreColour, scoreBg } = heatmapCellColours(pct, totalProjects > 0);

              const tooltipText = totalProjects > 0
                ? `Step ${stepNum}: ${count} of ${totalProjects} projects started (${pct}%)`
                : `Step ${stepNum}: no data yet`;

              return (
                // 3 cols on mobile, 4 on tablet, 6 on desktop (4 rows × 6 cells)
                <div className="col-4 col-md-3 col-lg-2" key={stepNum}>
                  <div
                    title={tooltipText}
                    style={{
                      background: scoreBg,
                      border: `1px solid #E2E8F0`,
                      borderLeft: `2px solid ${scoreColour}`,
                      borderRadius: 8,
                      padding: "10px 10px 8px",
                      minHeight: 80,
                      display: "flex",
                      flexDirection: "column",
                      cursor: "default",
                      transition: "box-shadow 150ms ease-out",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                    }}
                  >
                    {/* Step number */}
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: scoreColour,
                        fontFamily: "JetBrains Mono, monospace",
                        letterSpacing: "0.04em",
                        marginBottom: 2,
                      }}
                    >
                      {String(stepNum).padStart(2, "0")}
                    </div>

                    {/* Step title */}
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "#475569",
                        lineHeight: 1.3,
                        flex: 1,
                        marginBottom: 6,
                      }}
                    >
                      {title}
                    </div>

                    {/* Percentage */}
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: scoreColour,
                        fontFamily: "JetBrains Mono, monospace",
                        lineHeight: 1,
                      }}
                    >
                      {totalProjects > 0 ? `${pct}%` : "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Tiny legend dot + label
const LegendDot: React.FC<{ colour: string; bg: string; label: string }> = ({ colour, bg, label }) => (
  <div className="d-flex align-items-center gap-1">
    <div
      style={{
        width: 12,
        height: 12,
        borderRadius: 3,
        background: bg,
        border: `1px solid ${colour}55`,
        flexShrink: 0,
      }}
    />
    <span style={{ fontSize: 11, color: "#475569", fontWeight: 500, whiteSpace: "nowrap" }}>
      {label}
    </span>
  </div>
);

// ─── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: string;
  iconColor: string;
  iconBg: string;
  label: string;
  value: string;
  note?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconColor, iconBg, label, value, note }) => (
  <div
    className="card"
    style={{
      border: "1px solid #E2E8F0",
      borderRadius: 12,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      padding: "24px",
      background: "#FFFFFF",
    }}
  >
    <div
      className="d-flex align-items-center justify-content-center mb-4"
      style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background: iconBg,
      }}
    >
      <i className={`bi ${icon}`} style={{ fontSize: 22, color: iconColor }}></i>
    </div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 700,
        color: "#0F172A",
        fontFamily: "JetBrains Mono, monospace",
        lineHeight: 1.2,
        marginBottom: 4,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 13, fontWeight: 500, color: "#475569" }}>{label}</div>
    {note && (
      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{note}</div>
    )}
  </div>
);

// ─── FeedbackRow ──────────────────────────────────────────────────────────────

interface FeedbackRowProps {
  feedback: FeedbackEntry;
}

const FeedbackRow: React.FC<FeedbackRowProps> = ({ feedback }) => {
  const date = feedback.submittedAt
    ? new Date(feedback.submittedAt).toLocaleDateString("en-IE", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Unknown date";

  return (
    <div
      style={{
        padding: "16px 20px",
        borderRadius: 12,
        border: "1px solid #E2E8F0",
        background: "#F8FAFC",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>
            {feedback.projectTitle}
          </span>
          <span
            className="ms-2"
            style={{ fontSize: 11, color: "#94A3B8", fontFamily: "JetBrains Mono, monospace" }}
          >
            {feedback.uid.slice(0, 8)}…
          </span>
        </div>
        <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500, letterSpacing: "0.02em" }}>
          {date}
        </span>
      </div>
      <div className="row g-2">
        <div className="col-12 col-md-4">
          <FeedbackPill label="What's working" value={feedback.whatWorking} color="#475569" bg="#F8FAFC" />
        </div>
        <div className="col-12 col-md-4">
          <FeedbackPill label="Needs improvement" value={feedback.whatToImprove} color="#475569" bg="#F8FAFC" />
        </div>
        <div className="col-12 col-md-4">
          <FeedbackPill label="Key metric" value={feedback.keyMetric} color="#475569" bg="#F8FAFC" />
        </div>
      </div>
    </div>
  );
};

// ─── FeedbackPill ─────────────────────────────────────────────────────────────

interface FeedbackPillProps {
  label: string;
  value: string;
  color: string;
  bg: string;
}

const FeedbackPill: React.FC<FeedbackPillProps> = ({ label, value, color, bg }) => (
  <div
    style={{
      padding: "8px 12px",
      borderRadius: 8,
      background: bg,
      border: `1px solid ${color}22`,
    }}
  >
    <div style={{ fontSize: 10, fontWeight: 500, color, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.4 }}>
      {value || <span style={{ color: "#94A3B8" }}>—</span>}
    </div>
  </div>
);

// ─── UserList ─────────────────────────────────────────────────────────────────

interface UserProjectDetail {
  projectID: string;
  title: string;
  score?: number;
  projectProgress: number;
  flagged?: boolean;
}

interface UserDetailRowProps {
  user: UserRow;
}

const UserDetailRow: React.FC<UserDetailRowProps> = ({ user }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [projects, setProjects] = useState<UserProjectDetail[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [flaggingID, setFlaggingID] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState(user.adminNote ?? "");
  const [noteEditMode, setNoteEditMode] = useState(false);
  const [noteDraft, setNoteDraft] = useState(user.adminNote ?? "");
  const [savingNote, setSavingNote] = useState(false);

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await updateDoc(doc(db, "users", user.uid), { adminNote: noteDraft });
      setAdminNote(noteDraft);
      setNoteEditMode(false);
    } catch {
      // swallow — non-critical
    } finally {
      setSavingNote(false);
    }
  };

  const handleFlag = async (projectID: string, currentFlagged: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlaggingID(projectID);
    try {
      const projectRef = doc(db, "users", user.uid, "projects", projectID);
      await updateDoc(projectRef, { flagged: !currentFlagged });
      setProjects((prev) =>
        prev.map((p) => (p.projectID === projectID ? { ...p, flagged: !currentFlagged } : p))
      );
    } catch {
      // swallow — non-critical admin action
    } finally {
      setFlaggingID(null);
    }
  };

  const handleToggle = async () => {
    if (!expanded && !loaded) {
      setLoadingProjects(true);
      try {
        const projectsSnap = await getDocs(
          collection(doc(db, "users", user.uid), "projects")
        );
        const items: UserProjectDetail[] = projectsSnap.docs.map((d) => ({
          projectID: d.id,
          title: (d.data().projectTitle as string | undefined) ?? "Untitled",
          score: d.data().businessScore as number | undefined,
          projectProgress: (d.data().projectProgress as number | undefined) ?? 0,
          flagged: (d.data().flagged as boolean | undefined) ?? false,
        }));
        items.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
        setProjects(items);
        setLoaded(true);
      } catch {
        // swallow — projects unavailable
      } finally {
        setLoadingProjects(false);
      }
    }
    setExpanded((prev) => !prev);
  };

  const displayName =
    user.displayName || user.email || `${user.uid.slice(0, 8)}…`;

  return (
    <div style={{ borderBottom: "1px solid #F1F5F9" }}>
      {/* Row */}
      <div
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          cursor: "pointer",
          transition: "background 0.12s ease",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
      >
        {/* Avatar-ish icon */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 12,
            flexShrink: 0,
          }}
        >
          <i className="bi bi-person" style={{ fontSize: 15, color: "#475569" }}></i>
        </div>

        {/* Name / UID */}
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </div>
          {(user.displayName || user.email) && (
            <div
              style={{
                fontSize: 11,
                color: "#94A3B8",
                fontFamily: "JetBrains Mono, monospace",
              }}
            >
              {user.uid.slice(0, 12)}…
            </div>
          )}
        </div>

        {/* Project count */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#475569",
            marginRight: 16,
            whiteSpace: "nowrap",
          }}
        >
          {user.projectCount} project{user.projectCount !== 1 ? "s" : ""}
        </div>

        {/* Score indicator — plain text, no badge */}
        {!user.hasScore && (
          <span
            style={{
              fontSize: 11,
              color: "#94A3B8",
              marginRight: 12,
              whiteSpace: "nowrap",
            }}
          >
            No score
          </span>
        )}

        {/* Admin note badge */}
        {adminNote && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              padding: "2px 8px",
              borderRadius: 20,
              background: "#FEF3C7",
              color: "#92400E",
              marginRight: 12,
              whiteSpace: "nowrap",
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 0,
            }}
            title={adminNote}
          >
            <i className="bi bi-sticky me-1" style={{ fontSize: 10 }}></i>
            {adminNote.length > 40 ? adminNote.slice(0, 40) + "…" : adminNote}
          </span>
        )}

        {/* Expand chevron */}
        <i
          className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`}
          style={{
            fontSize: 13,
            color: "#94A3B8",
            transition: "transform 0.2s ease",
          }}
        ></i>
      </div>

      {/* Expanded project list */}
      {expanded && (
        <div
          style={{
            background: "#F8FAFC",
            padding: "12px 16px 16px 56px",
            borderTop: "1px solid #E2E8F0",
          }}
        >
          {/* View projects link */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => navigate(`/dashboard?uid=${user.uid}`)}
              style={{
                background: "transparent",
                border: "1px solid #BFDBFE",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: "#3B82F6",
                padding: "4px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
                transition: "background 0.12s ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#EFF6FF"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              title={`View projects as ${user.email ?? user.uid}`}
            >
              <i className="bi bi-box-arrow-up-right" style={{ fontSize: 13 }}></i>
              View projects
            </button>
          </div>

          {loadingProjects ? (
            <div className="d-flex align-items-center gap-2 py-2">
              <div className="spinner-border spinner-border-sm text-primary"></div>
              <span style={{ fontSize: 12, color: "#475569" }}>Loading projects…</span>
            </div>
          ) : projects.length === 0 ? (
            <p style={{ fontSize: 12, color: "#94A3B8", margin: "8px 0" }}>
              No projects found.
            </p>
          ) : (
            <div className="d-flex flex-column gap-2 py-1">
              {projects.map((p) => {
                const scoreColor =
                  p.score === undefined
                    ? "#94A3B8"
                    : p.score >= 70
                    ? "#10B981"
                    : p.score >= 40
                    ? "#F59E0B"
                    : "#EF4444";
                const scoreBg =
                  p.score === undefined
                    ? "#F1F5F9"
                    : p.score >= 70
                    ? "#D1FAE5"
                    : p.score >= 40
                    ? "#FEF3C7"
                    : "#FEE2E2";

                const isFlagging = flaggingID === p.projectID;

                return (
                  <div
                    key={p.projectID}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "14px 16px",
                      borderRadius: 8,
                      background: p.flagged ? "#FFF7F7" : "#FFFFFF",
                      border: `1px solid ${p.flagged ? "#FECACA" : "#E2E8F0"}`,
                    }}
                  >
                    <i
                      className="bi bi-folder2"
                      style={{ fontSize: 13, color: "#94A3B8", flexShrink: 0 }}
                    ></i>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#0F172A",
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.title}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#475569",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.projectProgress}/24 steps
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 20,
                        background: scoreBg,
                        color: scoreColor,
                        fontFamily: "JetBrains Mono, monospace",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.score !== undefined ? `${Math.round(p.score)}%` : "—"}
                    </span>
                    {/* Flag toggle */}
                    <button
                      type="button"
                      onClick={(e) => handleFlag(p.projectID, p.flagged ?? false, e)}
                      disabled={isFlagging}
                      title={p.flagged ? "Unflag this project" : "Flag this project"}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "2px 4px",
                        cursor: isFlagging ? "not-allowed" : "pointer",
                        color: p.flagged ? "#EF4444" : "#94A3B8",
                        fontSize: 14,
                        flexShrink: 0,
                        lineHeight: 1,
                        transition: "color 150ms ease-out",
                      }}
                    >
                      <i className={`bi ${isFlagging ? "bi-hourglass-split" : p.flagged ? "bi-flag-fill" : "bi-flag"}`}></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Admin Note section */}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #E2E8F0" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#475569",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="bi bi-sticky" style={{ fontSize: 12 }}></i>
              Admin Note
            </div>
            {noteEditMode ? (
              <div>
                <textarea
                  rows={2}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="e.g. VIP beta tester, churned, spam account"
                  style={{
                    width: "100%",
                    border: "1px solid #CBD5E1",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 13,
                    color: "#0F172A",
                    resize: "none",
                    outline: "none",
                    background: "#FFFFFF",
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#3B82F6"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; }}
                />
                <div className="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    disabled={savingNote}
                    onClick={handleSaveNote}
                    style={{
                      background: savingNote ? "#F1F5F9" : "#3B82F6",
                      color: savingNote ? "#94A3B8" : "#FFFFFF",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "5px 12px",
                      cursor: savingNote ? "not-allowed" : "pointer",
                    }}
                  >
                    {savingNote ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 2 }}></span>
                        Saving…
                      </>
                    ) : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setNoteDraft(adminNote); setNoteEditMode(false); }}
                    style={{
                      background: "transparent",
                      color: "#475569",
                      border: "1px solid #E2E8F0",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      padding: "5px 12px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: adminNote ? "#0F172A" : "#94A3B8",
                    fontStyle: adminNote ? "normal" : "italic",
                    flex: 1,
                  }}
                >
                  {adminNote || "No note — click to add one"}
                </span>
                <button
                  type="button"
                  onClick={() => { setNoteDraft(adminNote); setNoteEditMode(true); }}
                  style={{
                    background: "transparent",
                    border: "1px solid #E2E8F0",
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#475569",
                    padding: "3px 8px",
                    cursor: "pointer",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <i className="bi bi-pencil" style={{ fontSize: 11 }}></i>
                  Edit
                </button>
              </div>
            )}
          </div>
          {/* end::Admin Note */}
        </div>
      )}
    </div>
  );
};

// ─── UserList container ───────────────────────────────────────────────────────

interface UserListProps {
  users: UserRow[];
}

const UserList: React.FC<UserListProps> = ({ users }) => {
  const [search, setSearch] = useState("");

  if (users.length === 0) return null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? users.filter(
        (u) =>
          (u.displayName ?? "").toLowerCase().includes(q) ||
          (u.email ?? "").toLowerCase().includes(q) ||
          u.uid.slice(0, 12).toLowerCase().includes(q)
      )
    : users;

  return (
    <div
      className="card mb-8"
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      <div
        className="card-header border-0 d-flex flex-wrap align-items-start justify-content-between gap-3"
        style={{ padding: "20px 24px 12px" }}
      >
        <div>
          <span style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>
            Users
          </span>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 0, marginTop: 2 }}>
            Click a row to expand and view their projects.
            {q && (
              <span style={{ marginLeft: 6, color: "#3B82F6" }}>
                {filtered.length} of {users.length} shown
              </span>
            )}
          </p>
        </div>

        {/* Search input */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <i
            className="bi bi-search"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 13,
              color: "#94A3B8",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users…"
            style={{
              paddingLeft: 30,
              paddingRight: search ? 28 : 10,
              paddingTop: 6,
              paddingBottom: 6,
              fontSize: 13,
              border: "1px solid #CBD5E1",
              borderRadius: 8,
              outline: "none",
              background: "#F8FAFC",
              color: "#0F172A",
              width: 200,
              transition: "border-color 150ms ease-out, box-shadow 150ms ease-out",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "#3B82F6"; e.currentTarget.style.boxShadow = "0 0 0 3px #DBEAFE"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.boxShadow = "none"; }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#94A3B8",
                fontSize: 13,
                lineHeight: 1,
              }}
              aria-label="Clear search"
            >
              <i className="bi bi-x-circle-fill" />
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "0 0 4px" }}>
        {filtered.length === 0 ? (
          <div
            className="d-flex flex-column align-items-center text-center"
            style={{ padding: "32px 16px" }}
          >
            <i className="bi bi-person-x" style={{ fontSize: 32, color: "#94A3B8", marginBottom: 8 }}></i>
            <p style={{ fontSize: 14, color: "#475569", marginBottom: 0 }}>
              No users match &ldquo;{search}&rdquo;
            </p>
          </div>
        ) : (
          filtered.map((u) => (
            <UserDetailRow key={u.uid} user={u} />
          ))
        )}
      </div>
    </div>
  );
};

// ─── EmailQueueCard ───────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  sent:               { color: "#10B981", bg: "#D1FAE5" },
  pending:            { color: "#F59E0B", bg: "#FEF3C7" },
  failed:             { color: "#EF4444", bg: "#FEE2E2" },
  skipped_no_provider:{ color: "#94A3B8", bg: "#F1F5F9" },
};

const OnboardingNudgePreview: React.FC<{ item: EmailQueueItem }> = ({ item }) => {
  const firstName = item.firstName || "there";
  return (
    <div
      style={{
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        padding: "16px 20px",
        maxWidth: 520,
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", letterSpacing: "0.02em" }}>SUBJECT</span>
        <p style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", margin: "2px 0 0" }}>
          {item.subject || "Your business idea is waiting"}
        </p>
      </div>
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 8,
          padding: "16px",
          fontSize: 13,
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        <p style={{ marginBottom: 8 }}>Hi {firstName},</p>
        <p style={{ marginBottom: 8 }}>
          You signed up for Business Foundry yesterday — great first step! Your business idea is still waiting to be validated.
        </p>
        <p style={{ marginBottom: 16 }}>
          Take 10 minutes to complete your first step and get your idea scored by AI.
        </p>
        <a
          href="https://businessfactory.ai/app"
          onClick={(e) => e.preventDefault()}
          style={{
            display: "inline-block",
            background: "#3B82F6",
            color: "#FFFFFF",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Continue validating →
        </a>
        <p style={{ marginTop: 16, marginBottom: 0, fontSize: 11, color: "#94A3B8" }}>
          Sent from Business Foundry · hello@businessfactory.ai
        </p>
      </div>
    </div>
  );
};

const EmailQueueCard: React.FC<{ items: EmailQueueItem[] }> = ({ items }) => {
  const [expandedRowID, setExpandedRowID] = React.useState<string | null>(null);

  return (
    <div
      className="card"
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        marginBottom: 32,
      }}
    >
      <div
        className="card-header border-0 d-flex align-items-center justify-content-between"
        style={{ padding: "20px 24px 0" }}
      >
        <div>
          <span style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>
            Email Queue
          </span>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 0, marginTop: 2 }}>
            Onboarding nudge emails — up to 50 most recent, newest first. Click a row to preview.
          </p>
        </div>
      </div>

      <div className="card-body" style={{ padding: "16px 24px 24px" }}>
        {items.length === 0 ? (
          <div
            className="d-flex flex-column align-items-center text-center"
            style={{ padding: "32px 16px" }}
          >
            <i className="bi bi-envelope" style={{ fontSize: 48, color: "#94A3B8", marginBottom: 12 }}></i>
            <p style={{ fontSize: 15, color: "#475569", marginBottom: 0 }}>
              No emails queued yet.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Email", "Type", "Status", "Scheduled For", "Processed At"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "8px 12px",
                        color: "#94A3B8",
                        fontWeight: 600,
                        fontSize: 11,
                        borderBottom: "1px solid #E2E8F0",
                        whiteSpace: "nowrap",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const style = STATUS_STYLE[item.status] ?? { color: "#475569", bg: "#F1F5F9" };
                  const isExpanded = expandedRowID === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr
                        onClick={() => setExpandedRowID(isExpanded ? null : item.id)}
                        style={{
                          borderBottom: isExpanded ? "none" : "1px solid #F1F5F9",
                          cursor: "pointer",
                          background: isExpanded ? "#F8FAFC" : "transparent",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = "#F8FAFC";
                        }}
                        onMouseLeave={(e) => {
                          if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                        }}
                      >
                        <td style={{ padding: "10px 12px", color: "#0F172A", fontWeight: 500 }}>
                          {item.to}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#475569" }}>
                          {item.type}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              background: style.bg,
                              color: style.color,
                              borderRadius: 9999,
                              padding: "2px 10px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#94A3B8", whiteSpace: "nowrap" }}>
                          {item.scheduledFor ? timeAgo(item.scheduledFor) : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#94A3B8", whiteSpace: "nowrap" }}>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <span>{item.processedAt ? timeAgo(item.processedAt) : "—"}</span>
                            <i
                              className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`}
                              style={{ fontSize: 11, color: "#CBD5E1" }}
                            ></i>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid #F8FAFC" }}>
                          <td
                            colSpan={5}
                            style={{ padding: "0 10px 16px", background: "#F8FAFC" }}
                          >
                            {item.type === "onboarding_nudge" ? (
                              <OnboardingNudgePreview item={item} />
                            ) : (
                              <div
                                style={{
                                  padding: "12px 16px",
                                  border: "1px solid #E2E8F0",
                                  borderRadius: 8,
                                  fontSize: 13,
                                  color: "#94A3B8",
                                }}
                              >
                                No preview available for this email type.
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
